import { Component, Input, Output, EventEmitter, ElementRef, ViewEncapsulation, ViewChild } from '@angular/core';
import { OnInit, OnChanges, OnDestroy } from '@angular/core';
import { TaskService } from '../../core/service/task.service';
import { NGXLogger } from 'ngx-logger';
import { TaskUtility } from '../task.utility';
import { MemberService } from '../../core/service/member.service';
import { Member } from '../../member/model/member';
import moment from 'moment';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import copyToClipboard from 'copy-to-clipboard';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TranslateService } from '@ngx-translate/core';
import { TaskCategory } from 'src/app/case/model/task';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { DocumentComponent } from 'src/app/document/document.component';
import { DocumentService } from 'src/app/core/service/document.service';
import { saveAs } from '@progress/kendo-file-saver';
import { sortBy } from 'lodash';
import { Subscription } from 'rxjs';
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { User } from 'src/app/user/model/user';
import { UserService } from 'src/app/core/service/user.service';
import { Router } from '@angular/router';
import { animations } from 'src/app/core/utility/animations';
import { SettingService } from 'src/app/core/service/setting.service';
import { SendEmailComponent } from 'src/app/shared/send-document/send-document.component';
import { IntlService } from '@progress/kendo-angular-intl';
import { Channel } from 'src/app/channel/model/channel';
import { ChannelService } from 'src/app/core/service/channel.service';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { SessionStorageService } from 'ngx-webstorage';

@Component({
  standalone: false,
  selector: 'app-task-detail',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
  animations: [animations.fadeAnimation]
})
export class TaskDetailComponent implements OnInit, OnDestroy, OnChanges
{
  @ViewChild('sendEmail') public sendEmail: SendEmailComponent;
  @ViewChild('documentComponent') public documentComponent: DocumentComponent;
  @ViewChild('documentComponentViewer') public documentComponentViewer: DocumentComponent;

  @Input() public showDetails = false;
  @Input() public autoComplete = false;
  @Input() public isDialog = false;
  @Input() public gridContainerWidth = undefined;

  @Output() public completed = new EventEmitter<string>();
  @Output() public closeTaskDetail = new EventEmitter<string>();
  @Output() public followUpTaskAddedEvent = new EventEmitter<string>();
  @Output() public assigneeUpdated = new EventEmitter<string>();
  @Output() public dueDateUpdated = new EventEmitter<string>();
  @Output() public priorityUpdated = new EventEmitter<string>();

  public pageSizes = [5, 10, 25, 50, 100];
  public taskHistory: any[];
  public daysLeftBadgeClass: string;
  public assigneeDialogOpened = false;
  public taskDueDateOpened = false;
  public completeTaskDialogOpened = false;
  public servicesProvidedDialogOpened = false;
  public priorityDialogOpened = false;
  public forceCloseDialogOpened = false;
  public completingTask = false;
  public fullTaskName: string;
  public member: Member;
  public selectedOutcome: string;
  public taskCategory: TaskCategory;
  public taskDetails: any = null;
  public pageSize = 2;
  public view: GridViewManager;
  public count = 0;
  public taskDescription = '';
  public supportDocuments = [];
  public taskCreatedOnText: string;
  public taskDueOnText: string;
  public assessmentId: number;
  public assessmentName: string;
  public dropDownContainer = 'body';
  public updateNote = false;
  public documentUrl: string;
  public documentName: string;
  public contactHistoryDialogOpened = false;
  public assessmentDialogOpened = false;
  public userInGroupOrCareCoordinator = false;
  public isCareCoordinator = false;
  public isReferralCoordinator = false;
  public currentUser: User;
  public serviceCategories = [];
  public communityEnabled = false;
  public attachmentsEnabled = false;
  public accessMessages = false;
  public caseDialogOpened = false;
  public channelToEnroll: Channel;
  public groupedChannels: GroupResult[] = [];
  public notEnrolledChannelsFiltered: any[] = [];
  public notEnrolledChannels: Channel[];
  public searchTerms = '';
  public lastCompletedTaskCaseId = 0;
  public contactCodeset = undefined;
  public initiativeCodeset = undefined;
  public areaCodeset = undefined;
  public messageDialogOpened = false;
  public messageSendTo = '';
  public savingMessage = false;
  public feeScheduleId = undefined;
  public feeCategoryId = undefined;
  public checkAccumulatorOnly = undefined;

  /**
   * Calendar texts to use with moment translations
   */
  public calendarTexts: Array<{ text: string }> = [];
  public patientLabel: any = {};
  public taskCompleteText: string;

  private messagingSubscription: Subscription;
  private taskUpdatedText: string;
  private aTask: any;

  constructor(
    private taskService: TaskService,
    private memberService: MemberService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private documentService: DocumentService,
    private elem: ElementRef,
    private logger: NGXLogger,
    private taskUtilityService: TaskUtility,
    private assessmentService: AssessmentService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private router: Router,
    private settingService: SettingService,
    private channelService: ChannelService,
    private storageService: SessionStorageService,
    public intl: IntlService)
  {
    this.view = new GridViewManager(this.pageSize);
  }

  public get task(): any
  {
    return this.aTask;
  }

  public get isDisabledOutcomes(): boolean
  {
    return !this.member || this.completeTaskDialogOpened || this.assessmentDialogOpened || this.servicesProvidedDialogOpened;
  }

  @Input() public set task(task: any)
  {
    this.assessmentName = '';   // Reset assessment name
    this.calendarTexts = this.taskUtilityService.calendarTexts;
    this.aTask = task;
    this.getTaskText();

    this.daysLeftBadgeClass = this.taskUtilityService.daysLeftBadgeClass(this.aTask.daysLeft);
    this.aTask.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(this.aTask.priority);
    this.aTask.priority = this.taskUtilityService.priority(this.aTask.priority);
    this.aTask.caseType = this.aTask.member && this.aTask.channel ? `${this.aTask.member} - ${this.aTask.channel}` : null;
    if (this.aTask.category)
    {
      this.taskCategory = new TaskCategory();
      this.taskCategory = JSON.parse(this.aTask.category);
      if (this.taskCategory.assessment > 0)
      {
        this.setAssessmentName(this.taskCategory.assessment);
      }
    }
    else
    {
      this.taskCategory = new TaskCategory();
      this.taskCategory.dropdownoutcome = 'false';
      this.taskCategory.patientcontact = 'false';
      this.taskCategory.assessment = 0;
      this.taskCategory.feecategory = 0;
      this.taskCategory.feeschedule = 0;
    }

    this.getTaskOutcomes();
    this.getTaskHistory();
    this.getTaskDetails();

    // get member details
    this.member = null;
    this.memberService.getMember(task.memberId).subscribe({
      next: (member) =>
      {
        if (member !== null)
        {
          member.age = moment().diff(new Date(member.dateOfBirth), 'years');
          this.member = member;
          this.getChannelsData();

          // Check if we can auto-complete
          if (this.autoComplete && this.aTask.outcomes.length === 0)
          {
            this.showTaskCompletionDialog(undefined);
          }
        }
      }
    });

    this.supportDocuments = [];
    if (this.aTask.bpmId !== null && this.aTask.taskDefinitionKey !== null)
    {
      this.documentService.searchDocumentByBpmAndTaskDefinitionId(this.aTask.bpmId, this.aTask.taskDefinitionKey).subscribe({
        next: (documents) =>
        {
          documents.forEach(d =>
          {
            this.supportDocuments.splice(0, 0, d);
          });
        }
      });
    }
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.communityEnabled = sessionSetting.communityEnabled;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;
        this.accessMessages = (sessionSetting.messagesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;
      }
    });

    // When called from task list, the container will be body. On modal it will be inside the modal.
    this.dropDownContainer = this.isTaskListContext() || this.isReferralCoordinatorContext() ? 'body' : '';

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'taskHistoryUpdated')
        {
          this.getTaskHistory();
        }
      }
    });

    this.translate.get(['NOTES-SAVED', 'COMPLETE']).subscribe({
      next: (res) =>
      {
        this.taskUpdatedText = res['NOTES-SAVED'];
        this.taskCompleteText = res.COMPLETE;
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.updateNote)
    {
      this.taskService.updateTaskDetails(this.task.id, this.taskDetails).subscribe({
        next: (data) =>
        {
          const noteSavedDate = new Date(data.lastModifiedOn);
          this.messageHandlerService.displayDirectMessage('success',
            this.taskUpdatedText.replace('{{value}}', noteSavedDate.toLocaleString()));
        },
        error: (error) =>
        {
          this.logger.error(error);
        }
      });
    }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public ngOnChanges(): void
  {
    this.currentUser = this.authenticationService.currentUser;
    this.fullTaskName = this.aTask.name;
    this.taskDescription = this.aTask.description;
    if (this.currentUser)
    {
      this.isReferralCoordinator = this.currentUser.role === 'ROLE_REFERRAL_COORDINATOR';
    }
    this.checkSelfAssign();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * View document handler
   *
   * @param url Document Url
   * @param description Document description
   * @param mediaType Media mime type
   * @param createdOn Document created date
   */
  public viewHandler(url: string, description: string, mediaType: string, createdOn: string): void
  {
    this.documentComponentViewer.viewHandler(
      url,
      description,
      mediaType,
      createdOn
    );
  }

  public initView(): void
  {
    const elements = this.elem.nativeElement.querySelectorAll('.ProseMirror');
    for (let i = 0; i < elements.length; i++)
    {
      if (this.task.inCaseDetail)
      {
        elements[i].setAttribute('style', 'padding: 8px; max-height: 148px;');
      }
      else
      {
        elements[i].setAttribute('style', 'padding: 8px; max-height: 142px;');
      }
    }
  }

  /**
   * Close task details
   */
  public closeCompleteDialog(): void
  {
    if (this.completingTask) { return };
    this.completeTaskDialogOpened = false;
    if (this.autoComplete && this.aTask.outcomes.length === 0)
    {
      this.closeTaskDetail.emit();
    }
  }

  /**
   * Close Services Provided selection
   */
  public closeCompleteServicesProvidedDialog(): void
  {
    this.servicesProvidedDialogOpened = false;
    this.closeTaskDetail.emit();
  }

  // If we have created Contact History, then we're closing a contact task.
  public onContactHistoryAdded(): void
  {
    this.completeTask(this.selectedOutcome);
    this.contactHistoryDialogOpened = false;
  }

  /**
   * Event called when persisting recording services provided.
   */
  public onServicesProvidedAdded(): void 
  {
    this.completeTask(this.selectedOutcome);
    this.servicesProvidedDialogOpened = false;
  }

  /**
   * Contact history dialog cancelled.
   */
  public cancelServicesProvided(): void
  {
    this.servicesProvidedDialogOpened = false;
  }

  /**
   * Completes a task.
   *
   * @param outcome Task outcomes from the form attached to the task.
   */
  public completeTask(outcome: string): void
  {
    if (outcome !== undefined)
    {
      this.aTask.outcome = outcome;
    }

    this.completingTask = true;
    this.taskService.completeTask(this.aTask.id, this.aTask).subscribe({
      next: () =>
      {
        this.taskCompleted(this.aTask.id);
        this.messageHandlerService.displaySuccess('TASK-CLOSED-SUCCESSFULLY');
        this.storageService.store('newReferralDialogOpen', false);
        this.completingTask = false;
      },
      error: (error) =>
      {
        if (error !== null && error.error !== null && error.error.message !== null && error.error.message === 'transient.error-task-already-completed')
        {
          this.messageHandlerService.displayWarning('TASK-ALREADY-COMPLETED');
        }
        else
        {
          this.messageHandlerService.displayError(error);
        }
        this.completeTaskDialogOpened = false;
        this.completingTask = false;
        this.closeTaskDetail.emit();
      }
    });
  }

  /**
   * Updates a task's assignee.
   *
   * @param assignee Form of the task-assign component.
   */
  public updateTaskAssignee(assignee: any): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.assigneeDialogOpened = false;
    this.assigneeUpdated.emit(this.task);
    this.checkSelfAssign();
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   */
  public updateTaskDueDate(task: any): void
  {
    this.task.dueDate = task.dueDate;
    this.taskDueDateOpened = false;
    this.dueDateUpdated.emit(this.task);
    this.getTaskText();
  }

  /**
   * Updates a task's priority.
   *
   * @param task Selected task for re-prioritize.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskPriority(task: any): void
  {
    this.task.priorityNumber = task.priorityNumber;
    this.task.priority = task.priority;
    this.task.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(task.priority);
    this.priorityDialogOpened = false;
    this.priorityUpdated.emit(this.task);
  }

  public closeTaskDueDateDialog(): void
  {
    this.taskDueDateOpened = false;
  }

  public closeTaskPriorityDialog(): void
  {
    this.priorityDialogOpened = false;
  }

  public copyToClipboard(): void
  {
    this.translate.get([
      'TASK',
      'DESCRIPTION',
      'OPENED-ON',
      'DUE-ON',
      'PRIORITY',
      'CASE',
      'PROGRAM',
      'CHANNEL',
      'ASSIGNED-TO',
      'STATUS',
      'REFERRED-ORGANIZATION',
      'REFERRED-SERVICE',
      'CHANNEL-WORKFLOW-STATUS',
      'MEMBER',
      'CHANNELS-RECORD-NUMBER',
      'GENDER',
      'DOB',
      'HOME-PHONE',
      'BUSINESS-PHONE',
      'CELL-PHONE',
      'EMAIL',
    ]).subscribe({
      next: (res) =>
      {
        const texts = [];
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            if (key === 'MEMBER')
            {
              texts.push(this.patientLabel.patientLabel);
            }
            else
            {
              texts.push(res[key]);
            }
          }
        }

        const taskDescription = this.aTask.description === null ? '' : this.htmlToText(this.aTask.description);

        copyToClipboard(`
        ${texts[0]}: ${this.aTask.name === null ? '' : this.aTask.name}
        ${texts[1]}: ${taskDescription}
        ${texts[2]}: ${this.aTask.openedOn === null ? '' : this.intl.formatDate(this.intl.parseDate(this.aTask.openedOn), 'g')}
        ${texts[3]}: ${this.aTask.dueDate === null ? '' : this.intl.formatDate(this.intl.parseDate(this.aTask.dueDate), 'd')}
        ${texts[4]}: ${this.aTask.priority === null ? '' : this.aTask.priority}
        ${texts[5]}: ${this.aTask.caseName === null ? '' : this.aTask.caseName}
        ${texts[6]}: ${this.aTask.program === null ? '' : this.aTask.program}
        ${texts[7]}: ${this.aTask.channel === null ? '' : this.aTask.channel}
        ${texts[8]}: ${this.aTask.assigneeName === null ? '' : this.aTask.assigneeName}
        ${texts[9]}: ${this.aTask.status === null ? '' : this.aTask.status}
        ${texts[10]}: ${this.aTask.eventReferral ? this.aTask.eventReferral.referredToOrganizationName : ''}
        ${texts[11]}: ${this.aTask.eventReferral ? this.aTask.eventReferral.serviceType : ''}
        ${texts[12]}: ${this.aTask.channelWorkflowStatus === null ? '' : this.aTask.channelWorkflowStatus.displayName}
        ${texts[13]}: ${this.member === null || this.member.name === null || this.member.name.fullName === null ?
            '' : this.member.name.fullName}
        ${texts[14]}: ${this.member === null || this.member.mrn === null ? '' : this.member.mrn}
        ${texts[15]}: ${this.member === null || this.member.gender === null ? '' : this.member.gender.displayName}
        ${texts[16]}: ${this.member === null || this.member.dateOfBirth === null ?
            '' : this.intl.formatDate(this.intl.parseDate(this.member.dateOfBirth.toString()), 'd')}
        ${texts[17]}: ${this.member === null || this.member.contact === null || !this.member.contact.homePhoneNumber
            ? '' : this.member.contact.homePhoneNumber}
        ${texts[19]}: ${this.member === null || this.member.contact === null || !this.member.contact.cellPhoneNumber
            ? '' : this.member.contact.cellPhoneNumber}
        ${texts[20]}: ${this.member === null || this.member.contact === null || !this.member.contact.email
            ? '' : this.member.contact.email}`);
      }
    });
  }

  public noteChange(value: any): void
  {
    this.taskDetails.note = value;
    this.updateNote = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.assigneeDialogOpened = false;
  }

  public closeForceCloseDialog(): void
  {
    this.forceCloseDialogOpened = false;
  }

  /**
   * Save document to local
   *
   * @param url Document url
   * @param name Document file name
   */
  public saveFileLocal(url: string, name: string): void
  {
    this.documentService.retrieveDocument(url).subscribe({
      next: (data) =>
      {
        saveAs(`data:${data.mimeType};base64,${data.content}`, name);
      }
    });
  }

  /**
   * Follow-up Task has been added and emit event.
   */
  public followUpTaskAdded(): void
  {
    this.getTaskHistory();
    this.followUpTaskAddedEvent.emit();
  }

  public documentModified(): void
  {
    this.getTaskHistory();
  }

  /**
   * Contact history dialog cancelled.
   */
  public cancelContactHistory(): void
  {
    this.contactHistoryDialogOpened = false;
  }

  public getTaskText(): void
  {
    const now = new Date();
    if (this.aTask.dueDate != null)
    {
      const d1 = moment(Date.UTC(this.aTask.dueDate.getFullYear(), this.aTask.dueDate.getMonth(), this.aTask.dueDate.getDate()));
      const d2 = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      this.aTask.daysLeft = d1.diff(d2, 'days');
      this.aTask.taskDueOnText = this.getOnText(this.aTask.daysLeft);
    }

    if (this.aTask.openedOn != null)
    {
      const d1 = moment(Date.UTC(this.aTask.openedOn.getFullYear(), this.aTask.openedOn.getMonth(), this.aTask.openedOn.getDate()));
      const d2 = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      this.aTask.daysFrom = d1.diff(d2, 'days');
      this.aTask.taskCreatedOnText = this.getOnText(this.aTask.daysFrom);
    }
  }

  /**
   * Method called after assessment fulfillment has been added.
   */
  public afterAssessmentFulfillmentAdded(data: any): void
  {
    this.assessmentDialogOpened = false;
    this.messengerService.sendMessage(new MessageSubject('reloadMemberAssessments'));

    this.serviceCategories = data && data.serviceCategories ? data.serviceCategories : [];
    if (this.communityEnabled && this.serviceCategories.length > 0)
    {
      // Keep service categories for community referrals. completeTask() will be call later after the referral completion.
      this.processReferrals(this.serviceCategories);
    }
    else
    {
      this.completeTask(this.selectedOutcome);
    }
  }

  public assignToMe(): void
  {
    this.task.assignee = this.currentUser.userName;
    this.taskService.assignTask(this.task.id, this.task.assignee).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('TASK-ASSIGNEE-UPDATED-SUCCESSFULLY');
        this.messengerService.sendMessage(new MessageSubject('taskHistoryUpdated'));
        this.updateTaskAssignee({ name: this.currentUser.name, username: this.currentUser.userName });
      }
    });
  }

  /**
   * This method navigate to a specific Member Id
   *
   * @param memberId Member Id to navigate to.
   */
  public openMember(memberId: number): void
  {
    if (this.isTaskListContext())
    {
      this.router.navigate([`/patient/${memberId}`]);
    }
  }

  /**
   * If this is from task list, the member hyperlink will be enable.
   */
  public isTaskListContext(): boolean
  {
    return !this.isReferralCoordinator && this.router.url.includes('/tasks/');
  }

  public isReferralCoordinatorContext(): boolean
  {
    return this.router.url.includes('/referral-tasks/');
  }

  public onSendDocument(url: string, name: string): void
  {
    this.documentUrl = url;
    this.documentName = name;
    this.sendEmail.execute();
  }

  public openVideo(url: string): void
  {
    const win = window.open(url, '_blank');
    win.focus();
  }

  /**
   * After processing the Referral, the task will be completed.
   */
  public afterReferralProcessing(): void
  {
    this.completeTask(this.selectedOutcome);
  }

  public getChannelsData(): void
  {
    this.getUnenrolledChannels();
    this.channelToEnroll = undefined;
  }

  public cancelledCaseEnrollment(): void
  {
    this.caseDialogOpened = false;
    this.channelToEnroll = undefined;
  }

  /**
   * Filters the channel staff based on user input.
   */
  public filterNotEnrolledChannels(filter: string): void
  {
    this.searchTerms = filter;
    this.notEnrolledChannelsFiltered = this.notEnrolledChannels
      .filter(channel => channel.name.toLowerCase().includes(filter.toLowerCase()));
    this.setGroupedChannels();
  }

  /**
   * Create a new message.
   */
  public composeMessage(sendTo: string = null, isDirectAddress = false): void
  {
    this.messageSendTo = isDirectAddress ? `direct-${sendTo}` : sendTo;
    this.messageDialogOpened = true;
  }

  public forceClose(): void
  {
    this.forceCloseDialogOpened = false;
    this.completed.emit(this.aTask.id);
  }

  /**
   * Emits a task completed event.
   *
   * @param taskId Task ID
   */
  private taskCompleted(taskId: string)
  {
    this.completed.emit(taskId);

    // send message to update case flags (Case UI)
    this.messengerService.sendMessage(new MessageSubject('reloadMemberFlags'));
  }

  private showTaskCompletionDialog(outcome: any)
  {
    // Must not show assessment modal on failure.
    const isOnFailure = outcome !== undefined && outcome != null && (outcome.outcome === 'On Failure' || outcome.color === 'red');

    this.selectedOutcome = outcome;
    this.lastCompletedTaskCaseId = this.aTask.caseId;
    if (this.taskCategory.patientcontact === 'true' && this.member !== null)
    {
      this.contactCodeset = this.taskCategory.contactcodeset ? +this.taskCategory.contactcodeset : undefined;
      this.initiativeCodeset = this.taskCategory.initiativecodeset ? +this.taskCategory.initiativecodeset : undefined;
      this.areaCodeset = this.taskCategory.areacodeset ? +this.taskCategory.areacodeset : undefined;
      this.contactHistoryDialogOpened = true;
    }
    else if (this.taskCategory.assessment > 0 && this.member !== null && !isOnFailure)
    {
      this.assessmentId = this.taskCategory.assessment; // Automatically set the assessmentId based on stencil
      this.assessmentDialogOpened = true;
    }
    else if (this.taskCategory.feecategory > 0 && this.taskCategory.feeschedule > 0 && !isOnFailure) 
    {
      this.feeScheduleId = this.taskCategory.feeschedule;
      this.feeCategoryId = this.taskCategory.feecategory;
      this.checkAccumulatorOnly = this.taskCategory.checkaccumulator === 'true';
      this.servicesProvidedDialogOpened = true;
    }
    else
    {
      this.completeTaskDialogOpened = true;
    }
  }

  /**
   * get task history
   */
  private getTaskHistory()
  {
    this.view.startLoading();
    this.taskService.getTaskHistory(this.task.id).subscribe({
      next: (data) =>
      {
        data.forEach(d =>
        {
          d.time = new Date(d.time);
        });
        data = sortBy(data, ['time']).reverse();
        this.view.setData(data);
        this.count = data.length;
        this.taskHistory = data;
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  /**
   * get task details
   */
  private getTaskDetails()
  {
    this.taskService.getTaskDetails(this.task.id).subscribe({
      next: (data) =>
      {
        this.taskDetails = data;
        // If ad-hoc assessment, set the object with the assessmentId
        if (this.taskDetails.assessment !== null && this.taskDetails.assessment.id > 0)
        {
          this.taskCategory = new TaskCategory();
          this.taskCategory.assessment = this.taskDetails.assessment.id;
          // Set Assessment Name for ad-hoc Task Assessment
          this.assessmentName = `${this.taskDetails.assessment.name} - ${this.taskDetails.assessment.assessmentVersion}`;
        }
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  /**
   * get task details
   */
  private getTaskOutcomes()
  {
    this.taskService.getTaskOutcomes(this.task.bpmId, this.task.taskDefinitionKey, this.task.formKey, this.task.id).subscribe({
      next: (data) =>
      {
        this.task.outcomes = data;
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  /**
   * This returns the text content of the passed in html string. This is to strip all the html tags if any.
   *
   * @param html Html content
   */
  private htmlToText(html: string)
  {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * Set the Assessment Name to display when the assessment is set from the Modeler stencil.
   *
   * @param assessmentId Assessement Id
   */
  private setAssessmentName(assessmentId: number): void
  {
    this.assessmentService.getAssessment(assessmentId).subscribe({
      next: (assessment) =>
      {
        this.assessmentName = assessment.name + (assessment.assessmentVersion ? ' - v' + assessment.assessmentVersion : '');
      }
    });
  }

  private checkSelfAssign()
  {
    if (this.task.assignee === this.currentUser.userName)
    {
      this.userInGroupOrCareCoordinator = false;
    }
    else
    {
      if (this.task.channelId > 0)
      {
        this.userService.getUsersByChannelGroups(this.task.channelId).subscribe({
          next: (users) =>
          {
            this.userInGroupOrCareCoordinator = users.find(p => p.username === this.currentUser.userName) !== undefined;
          }
        });
      }
      // Ad hoc-task
      else
      {
        this.userService.getCacheUsersByRole('ROLE_CARE_COORDINATOR').subscribe({
          next: (users) =>
          {
            this.userInGroupOrCareCoordinator = users.find(p => p.username === this.currentUser.userName) !== undefined;
          }
        });
      }
    }
    this.userService.getCacheUsersByRole('ROLE_CARE_COORDINATOR').subscribe({
      next: (users) =>
      {
        this.isCareCoordinator = users.find(p => p.username === this.currentUser.userName) !== undefined;
      }
    });
  }

  /**
   * Return the proper text based on the number of days provided.
   *
   * @param days Number of days.
   */
  private getOnText(days: number): string
  {
    let daysOnText = '';
    if (days >= 1)
    {
      if (days === 1)
      {
        daysOnText = `(${this.calendarTexts[1].text})`;
      }
      else
      {
        daysOnText = `(${this.calendarTexts[5].text})`.replace('{{value}}', String(days));
      }
    }
    else if (days < 0)
    {
      if (days === -1)
      {
        daysOnText = `(${this.calendarTexts[2].text})`;
      }
      else
      {
        daysOnText = `(${this.calendarTexts[4].text})`.replace('{{value}}', String(Math.abs(days)));
      }
    }
    else if (days === 0)
    {
      daysOnText = `(${this.calendarTexts[0].text})`;
    }
    return daysOnText;
  }

  /**
   * Proceed with Community referral for the categories returned from the new/updated assessments.
   *
   * @param serviceCategories Array of service categories ids.
   */
  private processReferrals(serviceCategories: any[]): void
  {
    if (this.communityEnabled && serviceCategories !== null && serviceCategories.length > 0)
    {
      const messageObject = { categories: serviceCategories, context: 'task', parentCaseId: this.lastCompletedTaskCaseId };
      this.messengerService.sendMessage(new MessageSubject('searchForServicesToReferred', messageObject));
    }
  }

  private getUnenrolledChannels(): void
  {
    this.channelService.getNotEnrolledChannels(this.member.id).subscribe({
      next: (data) =>
      {
        this.notEnrolledChannels = data;
        this.notEnrolledChannels.forEach(c =>
        {
          c.programName = c.program.name;
        });
        this.notEnrolledChannelsFiltered = this.notEnrolledChannels.sort((a, b) => (a.name.localeCompare(b.name)));
        this.setGroupedChannels();
      },
      error: (error) =>
      {
        this.logger.error(error);
      }
    });
  }

  private setGroupedChannels(): void
  {
    // Build the grouped Channels
    const groupedChannels = groupBy(this.notEnrolledChannelsFiltered, [{ field: 'programName' }]);
    this.groupedChannels.forEach(d =>
    {
      const items = sortBy(d.items, ['name']);
      d.items = items;
    });
    this.groupedChannels = sortBy(groupedChannels, ['value']);
  }
}
